import { Component, Input, OnDestroy } from '@angular/core';
import { FormArray, FormBuilder, FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { firstValueFrom, Subscription } from 'rxjs';
import { AppState } from 'src/app/redux/app.state';
import { DeviceTypeEnum } from 'src/enums';
import { Product, ProductWithControls, User } from 'src/typings';
import { CatalogueComponent } from '../catalogue/catalogue.component';
import { ProductService } from '../product.service';

@Component({
  selector: 'app-product-analogue-list',
  templateUrl: './analogue-list.component.html',
  styleUrls: ['./analogue-list.component.scss']
})
export class AnalogueListComponent implements OnDestroy {
  @Input() set product(payload: Product | undefined) {
    this._product = payload;
    if (
      (this.product && this.product.analogies && this.product.analogies.length === 0) ||
      !this.product?.analogies
    ) {
      this.loadAnalogies();
    } else {
      this.analogiesFormArray = new FormArray(
        this.product.analogies.map((product) => {
          return new FormControl({ product });
        })
      );
      this.analogueSubscription = this.analogiesFormArray?.valueChanges.subscribe((data) => {
        this.handleAnalogueChanges(data);
      });
    }
  }

  private _deviceType: DeviceTypeEnum = DeviceTypeEnum.DESKTOP;

  get deviceType() {
    return this._deviceType;
  }

  set deviceType(payload: DeviceTypeEnum) {
    this._deviceType = payload;
    switch (payload) {
      case DeviceTypeEnum.DESKTOP:
        this.columnsToDisplay = [
          'id',
          'image',
          'description',
          'brand',
          'article',
          'analogies',
          'retail',
          ...this.priceControlColumns,
          'stock',
          'cell',
          'move',
          'marketPlace',
          'selector',
          'remove'
        ];
        break;
      case DeviceTypeEnum.LAPTOP:
        this.columnsToDisplay = [
          'id',
          'image',
          'combineDescription',
          'analogies',
          'retail',
          ...this.priceControlColumns,
          'stock',
          'cell',
          'move',
          'selector',
          'remove'
        ];
        break;
      case DeviceTypeEnum.TABLET:
        this.columnsToDisplay = [
          'image',
          'combineDescriptionWithId',
          'combineRetailWithStock',
          ...this.priceControlColumns,
          'analogies',
          'selector',
          'remove'
        ];
        break;
      case DeviceTypeEnum.MOBILE:
        this.columnsToDisplay = ['mobileRow'];
        break;
      default:
        this.columnsToDisplay = [
          'id',
          'image',
          'description',
          'brand',
          'article',
          'analogies',
          'retail',
          ...this.priceControlColumns,
          'stock',
          'cell',
          'selector',
          'remove'
        ];
        break;
    }
  }

  columnsToDisplay = [
    'id',
    'image',
    'description',
    'brand',
    'article',
    'analogies',
    'retail',
    'stock',
    'cell',
    'selector',
    'remove'
  ];

  analogiesFormArray: FormArray | undefined;

  loading: boolean = false;

  private _product: Product | undefined;

  get product() {
    return this._product;
  }

  storeSubscription: Subscription;

  user: User | undefined;

  analogueSubscription: Subscription | undefined;

  analogueGWSubscription: Subscription;

  loadingProducts: Product[] = [];

  constructor(
    private productService: ProductService,
    private store: Store<AppState>,
    private fb: FormBuilder,
    private dialog: MatDialog
  ) {
    this.storeSubscription = this.store.subscribe((data) => {
      this.user = data.user.info;
      this.deviceType = data.user.deviceType;
    });
    this.analogueGWSubscription = this.productService.analgogueGw.subscribe((data) => {
      if (!this.product) return;
      if (data.product_id === this.product.id) {
        this.updateAnalogies(data.list);
      }
    });
  }

  get isAdmin() {
    return this.user?.roles?.some((role) => role.description === 'admin') === true;
  }

  private get priceControlColumns() {
    return ['updateRetail'];
  }

  ngOnDestroy(): void {
    this.analogueSubscription?.unsubscribe();
    this.analogueGWSubscription.unsubscribe();
  }

  updateAnalogies(analogies: Product[]) {
    if (!this.product) return;
    this.product.analogies = analogies;
    if (!this.product || !this.product.analogies) return;
    const newAnalogies = analogies.filter((product) => {
      const findInArrayIndex: number = this.analogiesFormArray?.value.findIndex(
        (row: ProductWithControls) => {
          return row.product.id === product.id;
        }
      );
      return findInArrayIndex === -1;
    });
    const removedAnalogies: ProductWithControls[] = this.analogiesFormArray?.value.filter(
      (row: ProductWithControls) => {
        const findInArrayIndex: number = analogies.findIndex((product: Product) => {
          return row.product.id === product.id;
        });
        return findInArrayIndex === -1;
      }
    );

    newAnalogies?.forEach((product) => {
      this.analogiesFormArray?.push(new FormControl({ product }));
    });

    removedAnalogies?.forEach((row) => {
      const findIndex = this.analogiesFormArray?.value.findIndex((_row: ProductWithControls) => {
        return _row.product.id === row.product.id;
      });
      this.analogiesFormArray?.removeAt(findIndex);
    });
  }

  async loadAnalogies() {
    if (!this.product) return;
    this.loading = true;
    try {
      this.product.analogies = await firstValueFrom(
        this.productService.getAnalogues(this.product.id)
      );
      this.analogiesFormArray = new FormArray(
        this.product.analogies.map((product) => {
          return new FormControl({ product });
        })
      );
      this.analogueSubscription = this.analogiesFormArray?.valueChanges.subscribe((data) => {
        this.handleAnalogueChanges(data);
      });
    } catch (err) {
      console.error(err);
    }
    this.loading = false;
  }

  productInAnalogies(product: Product, analogies = this.product?.analogies) {
    if (!analogies) return false;
    const findIndex = analogies.findIndex((_product) => {
      return _product.id === product.id;
    });
    return findIndex > -1;
  }

  async handleAnalogueChanges(analogies: ProductWithControls[]) {
    if (!this.product || !this.product.analogies) return;
    const newAnalogies = analogies.filter((row) => {
      return !this.productInAnalogies(row.product);
    });
    const removedAnalogies = this.product.analogies.filter((product) => {
      return !this.productInAnalogies(
        product,
        analogies.map((row) => row.product)
      );
    });

    await Promise.all(
      newAnalogies.map((row) => {
        return this.addAnalogue(row.product);
      })
    );

    await Promise.all(
      removedAnalogies.map((product) => {
        return this.removeAnalogue(product);
      })
    );
  }

  getIndexOfLoadingProduct(product: Product) {
    return this.loadingProducts.findIndex((prod) => prod.id === product.id);
  }

  async addAnalogue(product: Product) {
    if (!this.product) return;
    const findIndexInLoadingArray = this.getIndexOfLoadingProduct(product);
    if (findIndexInLoadingArray > -1) return;
    this.loading = true;
    this.loadingProducts.push(product);
    try {
      await firstValueFrom(
        this.productService.addAnalogue({ product_id_1: this.product.id, product_id_2: product.id })
      );
    } catch (err) {
      console.error(err);
    }
    const findIndexInLoadingArray2 = this.getIndexOfLoadingProduct(product);
    this.loadingProducts.splice(findIndexInLoadingArray2, 1);
    this.loading = false;
  }

  async removeAnalogue(product: Product) {
    if (!this.product) return;
    const findIndexInLoadingArray = this.getIndexOfLoadingProduct(product);
    if (findIndexInLoadingArray > -1) return;
    this.loadingProducts.push(product);
    this.loading = true;
    try {
      await firstValueFrom(
        this.productService.removeAnalogue({
          product_id_1: this.product.id,
          product_id_2: product.id
        })
      );
    } catch (err) {
      console.error(err);
    }
    const findIndexInLoadingArray2 = this.getIndexOfLoadingProduct(product);
    this.loadingProducts.splice(findIndexInLoadingArray2, 1);
    this.loading = false;
  }

  openDialog() {
    if (!this.product || !this.product.analogies) return;
    const dialogRef = this.dialog.open(CatalogueComponent, {
      height: 'calc(100% - 48px)',
      width: '1500px'
    });
    dialogRef.componentInstance.type = 'selector';
    dialogRef.componentInstance.columnsToDisplay = [
      'id',
      'image',
      'descriptionSelector',
      'brand',
      'article',
      'analogies',
      'retail',
      'stock',
      'cell',
      'checkbox'
    ];
    dialogRef.componentInstance.selectedProductsFormArray = this.analogiesFormArray;
    dialogRef.componentInstance.loadingProductsInTable = this.loadingProducts;
  }
}
